import { makeAutoObservable } from "mobx";

import { ApiConnector } from "../api/api-connector";
import { clvVersion, devTools } from "../config/config";
import { hasMonitoringAccess, hasPermission } from "../constants/permissions";
import { AddPatient } from "../features/add-patient/add-patient";
import { AdminConsole } from "../features/admin/admin-console";
import { Patient } from "../features/patient/patient";
import { PatientSearchModel } from "../features/patients/patient-search-model";
import { TAB_DEFS } from "../features/patients/patients-tab-model";
import { PatientsTabs } from "../features/patients/patients-tabs";
import { assert } from "../utils/utils";

export class AppModel {
  clvVersion = clvVersion ?? "-";
  darkMode = localStorage.getItem("darkMode") === "true";
  devToolsEnabled = devTools;
  devToolsOpen = false;

  // basics
  patientSearch = new PatientSearchModel();
  patientsTabs: PatientsTabs | null = null;

  // patient loading and tabs
  patient: Patient | null = null;

  // admin
  adminShit: AdminConsole | null = null;

  constructor(public seerlinqApi: ApiConnector) {
    makeAutoObservable(this);
  }

  // basic loaders
  init() {
    this.setTheme();
  }

  async initPatientsTabs(initialTab?: string) {
    const permissions = this.seerlinqApi.permissions;
    const userUUID = this.seerlinqApi.requireUserUUID();
    const visibleDefs = TAB_DEFS.filter(
      (def) =>
        !def.requiredPermission ||
        hasPermission(permissions, def.requiredPermission),
    );
    this.patientsTabs = new PatientsTabs(
      permissions,
      userUUID,
      visibleDefs,
      initialTab,
    );
    await this.patientsTabs.loadActiveTab();
  }

  initPatient(patientId: number) {
    if (!this.patient || this.patient.patientId !== patientId) {
      this.patient = new Patient(patientId, this.seerlinqApi);
    }

    return this.patient;
  }

  requirePatient(): Patient {
    assert(this.patient, "Patient object is missing");
    return this.patient;
  }

  requireAdminShit(): AdminConsole {
    assert(this.adminShit, "Admin object is missing");
    return this.adminShit;
  }

  async initAdmin() {
    if (hasMonitoringAccess(this.seerlinqApi.permissions)) {
      this.adminShit = new AdminConsole(this.seerlinqApi);
      await this.adminShit.init();
    }
  }

  async initAddPatient() {
    const model = new AddPatient(this.seerlinqApi);
    await model.initEmpty();
    return model;
  }

  // dark mode
  setTheme() {
    document.documentElement.classList.toggle("dark", this.darkMode);
  }

  async toggleDarkMode() {
    this.darkMode = !this.darkMode;
    localStorage.setItem("darkMode", String(this.darkMode));
    this.setTheme();
  }

  setDevToolsOpen(open: boolean) {
    this.devToolsOpen = open;
  }
}
